import type { ConnectOutcome } from "@/lib/api";
import {
  CONNECT_OUTCOME_PARAM,
  connectOutcomeMessage,
  parseConnectOutcome,
  takeConnecting,
} from "@/lib/connect-return";
import { HUB_ACCOUNTS_KEY } from "@/lib/hub-accounts";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";

// A connect redirect came back to this page with ?integrations=<outcome>:
// refresh the account lists, say how it went, and clean the address. Mount
// once per page that connect flows return to (same shape as
// useConnectionOutcomes for the chat apps). The route must declare the param
// in validateSearch or the router drops it before the page mounts. Returns
// the outcome read on first render so the page can react synchronously (the
// onboarding step uses it to stay put instead of auto-advancing).
export function useConnectReturn(): ConnectOutcome | null {
  const queryClient = useQueryClient();
  const [outcome] = useState(() =>
    parseConnectOutcome(new URL(window.location.href).searchParams.get(CONNECT_OUTCOME_PARAM)),
  );

  useEffect(() => {
    if (!outcome) return;
    // Only the run that strips the param from the address reports it. A
    // re-run (StrictMode mounts effects twice) finds the address clean and
    // does nothing, so there is exactly one toast and one refetch.
    const url = new URL(window.location.href);
    if (!url.searchParams.has(CONNECT_OUTCOME_PARAM)) return;
    url.searchParams.delete(CONNECT_OUTCOME_PARAM);
    window.history.replaceState(window.history.state, "", url.pathname + (url.search || ""));
    const { kind, text } = connectOutcomeMessage(outcome, takeConnecting());
    if (kind === "success") toast.success(text);
    else if (kind === "error") toast.error(text);
    else toast(text);
    if (outcome !== "cancelled") queryClient.invalidateQueries({ queryKey: HUB_ACCOUNTS_KEY });
  }, [outcome, queryClient]);

  return outcome;
}
